import { TLoadDependeciesParams } from '@df/catalog/model';
import { Product as CoreProduct, IProductData, IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { NgInject } from '@df/ng/ng-inject';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { NProductViewCalculatorModal } from './view/calculator/modal/product-view-calculator-modal.component';
import { NProductViewDetailsQuoteAttributes } from './view/details/quote/attributes/product-view-details-quote-attributes.component';
import { NProductViewImageSizesModal } from './view/image-sizes-modal/product-view-image-sizes-modal.component';
import { NProductViewLayout } from './view/layout/product-view-layout.namespace';
import { NProductViewPriceListModal } from './view/price-list-modal/product-view-price-list-modal.component';
import { NProductViewRequestQuoteModal } from './view/request-quote/modal/product-view-request-quote-modal.component';
import { NICmsOutletBlockComponent } from '../outlet-block/icms-outlet-block.component';
import uniq from 'lodash-es/uniq';
import { EDGE_TYPE_ATTRIBUTE_ID } from './view/configurable-options/product-view-configurable-options.component';
import { NProductPlpTrustpilotReviewsConfig } from './plp-trustpilot-reviews/config/product-plp-trustpilot-reviews.config.namespace';
import { ConfigResource } from '@df/catalog/config/config.resource';

declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        /**
         * Product color
         */
        color?: number | number[];
        /**
         * Alternative image used on listing page on hover
         */
        img_alternative?: string;
        /**
         * Horizontal image that can be used on 2-col grid
         */
        img_horizontal?: string;
        /**
         * Text under product name used on listing page for banners
         */
        banner_subtitle?: string;
        /**
         * Link that banner provides
         */
        banner_url?: string;
        banner_cta?: string;
        banner_overlay?: boolean;
        short_description?: string;
        has_map?: boolean;
        map_price?: number;
        max_price?: number | undefined;
        max_org_price?: number | undefined;
        thickness?: number;
        brand?: number;
        fabric_style?: number | number[];
        sample_type?: ESampleType;
        is_cartable?: 1 | 0;
        image_alt_text?: string;
        children_skus?: string[];
    }

    interface IProductDataV3 {
        /**
         * Id of the product page layout (used with cms block)
         */
        product_page?: string;
        product_nrc?: number;
        nrc_calculator?: number;
        product_features?: number[];
        documentation?: number[];
        sold_by?: number;
        is_complex_panel?: 1 | 0;
        returnpolicy?: string;
        is_art_panel?: 1 | 0;
        variation_description?: string;
        moq?: number;
        qty_increments?: number;
        pitchPrintDesignId?: string;
        attachments?: IProductAttachment[];
        pricing_modal?: string;
        prop_65?: boolean;
        prop_65_notice?: string;
        swatch_request?: boolean;
    }
}

export interface IProductAttachment {
    label: string;
    file: string;
    type: string;
}

export enum EProductType {
    complex = 'complex',
    artPanel = 'art panel',
    quote = 'quote',
    standard = 'standard'
}

export enum ESampleType {
    fabric = 7510
}

/**
 * Colors map entry type
 */
export interface IColorMapEntry {
    color: number;
    parent: number;
}

export class Product<T extends IProductData = IProductData> extends CoreProduct<T> {
    /**
     * Load cms block servoce
     */
    @NgInject(ICmsBlockResource)
    private iCmsBlockResource!: ICmsBlockResource;

    @NgInject(ConfigResource)
    private configResource!: ConfigResource;

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const safeParams = { ...params, safe: true };
        if (this.verbosity > 1) {
            params.loader.add(this.configResource.loadModelByUrl(NProductPlpTrustpilotReviewsConfig.ID, safeParams));
        }
        if (this.verbosity > 2) {
            params.loader.add(
                this.iCmsBlockResource.retrieve({ bareUrl: NProductViewLayout.BLOCK_ID, variant: this.ppLayoutVariant }, safeParams)
            );
            if (this.displayRoomCalculator) {
                params.loader.add(this.iCmsBlockResource.retrieve({ bareUrl: NProductViewCalculatorModal.BLOCK_ID }, safeParams));
            }
            if (this.displayPriceList) {
                params.loader.add(
                    this.iCmsBlockResource.retrieve(
                        { bareUrl: NProductViewPriceListModal.BLOCK_ID, variant: this.parent.id.toString() },
                        safeParams
                    )
                );
            }
            if (this.displayImagesSizes) {
                params.loader.add(
                    this.iCmsBlockResource.retrieve(
                        { bareUrl: NProductViewImageSizesModal.BLOCK_ID, variant: this.parent.id.toString() },
                        safeParams
                    )
                );
            }
            if (this.isQuote) {
                params.loader.add(this.iCmsBlockResource.retrieve({ bareUrl: NProductViewDetailsQuoteAttributes.BLOCK_ID }, safeParams));
                params.loader.add(
                    this.iCmsBlockResource.retrieve(
                        { bareUrl: NProductViewRequestQuoteModal.BLOCK_ID, variant: this.requestModalVariant },
                        safeParams
                    )
                );
            }

            if (this.isComplexPanel) {
                const crosssellIds = (this.data as IProductDataV3)?.link?.crossSell;
                if (crosssellIds?.length) {
                    params.loader.add(this.app.resources.product.loadAllIds(crosssellIds, 2, safeParams));
                }
            }
            params.loader.add(
                this.iCmsBlockResource.retrieve(
                    { bareUrl: NICmsOutletBlockComponent.BLOCK_ID, variant: this.pdpComponentsOutletVariant },
                    safeParams
                )
            );
        }
    }

    /**
     * Product page layout variant
     *
     * Component inheritance logic:
     * - Simple product with no parent: uses own ID (components isolated to this product)
     * - Simple product with parent: uses parent ID (components shared with all siblings)
     * - Parent product: uses own ID (components shared with all children)
     */
    get ppLayoutVariant(): string {
        if (this.hasV3) {
            const pdpTemplate = (<IProductDataV3>this.data).product_page || (<IProductDataV3>this.parent.data)?.product_page;
            if (pdpTemplate) {
                return pdpTemplate;
            }
        }
        return 'default';
    }

    get pdpComponentsOutletVariant(): string {
        if (this.isSimple && this.parent) {
            return this.parent.id.toString();
        } else {
            return this.id.toString();
        }
    }

    get requestModalVariant(): string {
        return this.id.toString() + '-request-quote-modal';
    }

    get hasColorOption(): boolean {
        const data = this.data as IProductDataV2;
        if (data && !data.isBanner && this.isConfigurable && data.options?.configurable) {
            return !!data.options?.configurable['color'];
        }
        return false;
    }

    /**
     * Size guide block variant
     */
    get sizeGuideVariant(): string | undefined {
        return 'default';
    }

    /**
     * Delivery and returns guide block variant
     */
    get deliveryVariant(): string | undefined {
        return 'default';
    }

    /**
     * Use this to set force priority attributes order
     */
    protected override getConfigurableAttributeCodesOrder() {
        return ['color'];
    }

    get isComplexPanel(): boolean {
        return this.productType === EProductType.complex;
    }

    get isArtPanel(): boolean {
        return this.productType === EProductType.artPanel;
    }

    get isQuote(): boolean {
        return this.productType === EProductType.quote;
    }

    get isStandard(): boolean {
        return this.productType === EProductType.standard;
    }

    get displayPriceList(): boolean {
        return this.hasPricingModalData;
    }

    get displayImagesSizes(): boolean {
        return this.isArtPanel;
    }

    get displayRoomCalculator(): boolean {
        return !this.isQuote;
    }

    get hasPricingModalData(): boolean {
        return !!this.getAttr('pricing_modal');
    }

    private get productType(): EProductType {
        switch (true) {
            case !!this.getAttr('is_complex_panel'):
                return EProductType.complex;
            case !!this.getAttr('is_art_panel') && !!this.pitchPrintDesignId:
                return EProductType.artPanel;
            case !this.getAttr('is_cartable'):
                return EProductType.quote;
            default:
                return EProductType.standard;
        }
    }

    get pitchPrintDesignId(): string | undefined {
        const designId = this.getAttr('pitchPrintDesignId') as string | undefined;
        if (designId?.toLowerCase() === 'none') {
            return undefined;
        }
        return designId;
    }

    get imageAltText(): string | undefined {
        return (this.data as IProductDataV2)?.image_alt_text || this.data?.name;
    }

    get plpTrustpilotReviewsConfigData(): NProductPlpTrustpilotReviewsConfig.TData | undefined {
        return this.configResource.getModelByUrl(NProductPlpTrustpilotReviewsConfig.ID)?.data;
    }

    getTrustpilotSkus(): string {
        const parent = this.parent as Product<IProductDataV3>;
        const children = parent?.children as Product<IProductDataV3>[];
        let skus: string[] = [];
        if (parent.data?.children_skus) {
            skus = parent.data?.children_skus;
        } else {
            let skusProducts: Product[] = [];
            if (children.length > 300) {
                const attrToGroupBy =
                    parent.configurableAttributeCodes?.indexOf(EDGE_TYPE_ATTRIBUTE_ID) !== -1
                        ? EDGE_TYPE_ATTRIBUTE_ID
                        : parent.configurableAttributeCodes?.[0];
                if (attrToGroupBy) {
                    skusProducts = children.filter(i => i.data?.[attrToGroupBy] === this.data?.[attrToGroupBy]);
                } else {
                    skusProducts = (children || []).slice(0, 300);
                }
            } else {
                skusProducts = children || [];
            }
            skus = skusProducts.map(i => i?.data?.sku).filter((i): i is string => !!i);
        }
        return uniq([this.data?.sku, parent.data?.sku, ...skus]).join(',');
    }

    get trustpilotAvailability(): string | undefined {
        const availability = this.app.meta.getProductAvailability(this.id);
        if (availability) {
            return availability.split('/').pop();
        }
        return undefined;
    }

    protected get moq(): number {
        const moqValue = this.moqValue;
        const qtyIncrements = this.qtyIncrements;
        return Math.ceil(moqValue / qtyIncrements) * qtyIncrements;
    }

    protected get moqValue(): number {
        const moq = this.getAttr('moq') as string;
        if (moq) {
            if (typeof moq === 'number') {
                return moq;
            }
            const number = parseInt(moq);
            if (number) {
                return number;
            }
        }
        return 1;
    }

    protected get qtyIncrements(): number {
        const qtyIncrements = this.getAttr('qty_increments');
        if (qtyIncrements && typeof qtyIncrements === 'number') {
            return qtyIncrements;
        }
        return 1;
    }
}

ProductResource.instance = Product;
